import React, { useContext, useEffect, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import Cookies from "js-cookie";
import { UserContext } from "../common/UserContext";
import OrderChart from "./OrderChart";
import UserChart from "./UserChart";
import ShipmentChart from "./ShipmentChart";
import SCPChart from "./SCPChart";
import Cards from "./Cards";
import RoutingChart from "./RoutingChart";
import LogisticChart from "./LogisticChart";
import InventoryChart from "./InventoryChart";
import config from "../../envconfig";
import { useNavigate } from "react-router-dom";
import techtrio from "../../assest/images/techtrio.png";
import Widget from "./Widget";

const ResponsiveReactGridLayout = WidthProvider(Responsive);

const ReactGridLayout = () => {
  const { UM_UserId, UM_UserType } = useContext(UserContext);

  const [layouts, setLayouts] = useState(null);
  const [widgetDetails, setWidgetDetails] = useState([]);
  const [isAdmin, setisAdmin] = useState(false);
  const [roles, setRoles] = useState([]);
  const [Utype, setUtype] = useState();
  const [widgetArray, setWidgetArray] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Enhanced fullscreen logic
  useEffect(() => {
    let fullscreenRequested = false;

    const enterFullscreen = async () => {
      try {
        if (document.fullscreenElement) {
          setIsFullscreen(true);
          return;
        }
        
        const element = document.documentElement;
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }
        setIsFullscreen(true);
        fullscreenRequested = true;
      } catch (error) {
        console.log('Fullscreen error:', error);
      }
    };

    const exitFullscreen = () => {
      if (document.fullscreenElement && fullscreenRequested) {
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(err => console.log('Exit error:', err));
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen().catch(err => console.log('Exit error:', err));
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen().catch(err => console.log('Exit error:', err));
        }
      }
      setIsFullscreen(false);
    };

    // Enter fullscreen when component mounts
    enterFullscreen();

    // Handle fullscreen change events
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || 
                               document.webkitFullscreenElement || 
                               document.msFullscreenElement;
      setIsFullscreen(!!fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
      exitFullscreen();
    };
  }, []);

  // Refresh function for widget changes
  const handleWidgetChange = () => {
    console.log("Refreshing dashboard...");
    setRefreshKey(prevKey => prevKey + 1);
  };

  // Array comparison
  const arraysEqual = (arr1, arr2) => {
    console.log("Comparing Arrays:", arr1, arr2);
    if (arr1.length !== arr2.length) {
      console.log("Arrays not equal: Different lengths");
      return false;
    }
    const sortedArr1 = arr1.slice().sort();
    const sortedArr2 = arr2.slice().sort();
    const result = sortedArr1.every(
      (value, index) => value === sortedArr2[index]
    );
    console.log("Arrays equal?", result);
    return result;
  };

  useEffect(() => {
    console.log("Setting user type:", UM_UserType);
    setUtype(parseInt(UM_UserType));
  }, [UM_UserType]);

  // Main data fetching effect
  useEffect(() => {
    console.log("useEffect -> Initial Load for User:", UM_UserId);
    fetchWidgetData();
  }, [UM_UserId, refreshKey]);

  const fetchWidgetData = () => {
    const savedLayouts = Cookies.get(`${UM_UserId}`);
    console.log("Saved Layout from Cookies:", savedLayouts);

    if (savedLayouts && savedLayouts !== "false") {
      try {
        const parsedLayouts = JSON.parse(savedLayouts);
        console.log("Parsed Layouts:", parsedLayouts);
        setLayouts(parsedLayouts);

        const transformedWidgets = parsedLayouts.map((layout) => ({
          i: layout.i,
          x: layout.x,
          y: layout.y,
          w: layout.w,
          h: layout.h,
          type: layout.type,
        }));
        
        console.log("Transformed Widgets from Cookie:", transformedWidgets);
        setWidgetArray(transformedWidgets);
        
        const typesArray = transformedWidgets.map((widget) => widget.type);
        console.log("Widget Types Array:", typesArray);

        fetchWidgetDetails(typesArray);
      } catch (error) {
        console.error("Error parsing saved layouts:", error);
        fetchWidgetDetailsElse();
      }
    } else {
      console.log("No saved layouts -> Calling fetchWidgetDetailsElse()");
      fetchWidgetDetailsElse();
    }
    setInitialLoad(false);
  };

  const fetchWidgetDetails = async (typesArray) => {
    try {
      console.log("Fetching widget details for user:", UM_UserId);
      const response = await fetch(
        `${config.API_URL}/getWidgetsByUserId/${UM_UserId}`
      );
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      console.log("Fetched Widget Data:", data);

      const chartsArray = [];
      data.forEach((widget) => {
        if (widget.Widget_Order === 1) chartsArray.push("OrderChart");
        if (widget.Widget_User === 1) chartsArray.push("UserChart");
        if (widget.Widget_Shipment === 1) chartsArray.push("ShipmentChart");
        if (widget.Widget_Scp === 1) chartsArray.push("SCPChart");
        if (widget.Widget_Routing === 1) chartsArray.push("RoutingChart");
        if (widget.Widget_Inventory === 1) chartsArray.push("InventoryChart");
        if (widget.Widget_Logistic === 1) chartsArray.push("LogisticChart");
      });

      console.log("Charts Array from DB:", chartsArray);

      const areEqual = arraysEqual(chartsArray, typesArray);
      console.log("Do DB widgets match cookie widgets?", areEqual);

      if (!areEqual) {
        console.log("Mismatch -> Calling fetchWidgetDetailsElse()");
        fetchWidgetDetailsElse();
      }
    } catch (error) {
      console.error("Failed to fetch widgets:", error.message);
      fetchWidgetDetailsElse();
    }
  };

  function updateWidget(UM_UserId, columnId, isChecked) {
    console.log(
      `Updating widget -> User: ${UM_UserId}, Column: ${columnId}, Value: ${isChecked}`
    );
    const data = {
      Widget_UserId: UM_UserId,
      ColumnName: columnId,
      Value: isChecked ? 1 : 0,
    };
    fetch(`${config.API_URL}/update-widget`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    })
      .then((response) => response.json())
      .then((data) => console.log("Widget Update Success:", data))
      .catch((error) => console.error("Widget Update Error:", error));
  }

  const fetchWidgetDetailsElse = async () => {
    console.log("⚡ fetchWidgetDetailsElse CALLED for user:", UM_UserId);

    if (UM_UserId) {
      try {
        console.log("Fetching fallback widget details...");
        const response = await fetch(
          `${config.API_URL}/getWidgetsByUserId/${UM_UserId}`
        );
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        console.log("Fallback Widget Data:", data);

        setWidgetDetails(data);
        let widgets = [];

        if (data.length === 0) {
          console.log("No widgets found -> Fetching roles...");
          try {
            const response = await fetch(
              `${config.API_URL}/getRolesByUserId/${UM_UserId}`
            );
            const data = await response.json();
            console.log("Roles Data:", data);

            setisAdmin(UM_UserType == 1);
            setRoles(data);
            setisAdmin(parseInt(UM_UserType) === 1);
            if (parseInt(UM_UserType) === 1) {
              console.log("Admin detected -> Inserting default widgets...");
              fetch(`${config.API_URL}/Insertwidgets`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ UM_UserId }),
              })
                .then((response) => response.json())
                .then((data) => {
                  console.log("InsertWidgets Success:", data);
                  updateWidget(UM_UserId, "Widget_Order", true);
                  updateWidget(UM_UserId, "Widget_Shipment", true);
                  updateWidget(UM_UserId, "Widget_Scp", true);
                  updateWidget(UM_UserId, "Widget_User", true);
                })
                .catch((error) => console.error("InsertWidgets Error:", error));

              widgets = [
                { i: "widget1", x: 0, y: 0, w: 4, h: 2, type: "OrderChart" },
                { i: "widget2", x: 4, y: 0, w: 4, h: 2, type: "UserChart" },
                { i: "widget3", x: 0, y: 2, w: 4, h: 2, type: "ShipmentChart" },
                { i: "widget4", x: 4, y: 2, w: 4, h: 2, type: "SCPChart" },
              ];
            } else {
              console.log("Normal user -> Inserting widgets based on roles...");
              fetch(`${config.API_URL}/Insertwidgets`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ UM_UserId }),
              })
                .then((response) => response.json())
                .then((data) =>
                  console.log("InsertWidgets Normal User Success:", data)
                )
                .catch((error) =>
                  console.error("InsertWidgets Normal User Error:", error)
                );

              if (
                data.some((role) => role.RLM_MenuOptions.startsWith("Order"))
              ) {
                updateWidget(UM_UserId, "Widget_Order", true);
                widgets.push({
                  i: "widget1",
                  x: 0,
                  y: 4,
                  w: 8,
                  h: 2,
                  type: "OrderChart",
                });
              }
              if (
                data.some((role) =>
                  role.RLM_SubMenuOptions.startsWith("Shipment Master")
                )
              ) {
                updateWidget(UM_UserId, "Widget_Shipment", true);
                updateWidget(UM_UserId, "Widget_Inventory", true);
                widgets.push({
                  i: "widget2",
                  x: 4,
                  y: 0,
                  w: 4,
                  h: 2,
                  type: "ShipmentChart",
                });
                widgets.push({
                  i: "widget3",
                  x: 0,
                  y: 2,
                  w: 4,
                  h: 2,
                  type: "InventoryChart",
                });
              }
              if (
                data.some((role) =>
                  role.RLM_SubMenuOptions.startsWith("Logistic Party Master")
                )
              ) {
                updateWidget(UM_UserId, "Widget_Logistic", true);
                widgets.push({
                  i: "widget4",
                  x: 4,
                  y: 2,
                  w: 4,
                  h: 2,
                  type: "LogisticChart",
                });
              }
              if (
                data.some((role) =>
                  role.RLM_SubMenuOptions.startsWith("Route Master")
                )
              ) {
                updateWidget(UM_UserId, "Widget_Routing", true);
                widgets.push({
                  i: "widget5",
                  x: 0,
                  y: 0,
                  w: 4,
                  h: 2,
                  type: "RoutingChart",
                });
              }
            }
            console.log("Final Widgets for User:", widgets);
            setWidgetArray(widgets);
          } catch (error) {
            console.error("Error fetching roles:", error);
          }
        } else {
          console.log("User already has widgets:", data);
          widgets = [];

          if (data[0].Widget_Order === 1)
            widgets.push({
              i: "widget1",
              x: 0,
              y: 0,
              w: 4,
              h: 2,
              type: "OrderChart",
            });
          if (data[0].Widget_Shipment === 1)
            widgets.push({
              i: "widget2",
              x: 4,
              y: 0,
              w: 4,
              h: 2,
              type: "ShipmentChart",
            });
          if (data[0].Widget_Inventory === 1)
            widgets.push({
              i: "widget3",
              x: 0,
              y: 2,
              w: 4,
              h: 2,
              type: "InventoryChart",
            });
          if (data[0].Widget_Logistic === 1)
            widgets.push({
              i: "widget4",
              x: 4,
              y: 2,
              w: 4,
              h: 2,
              type: "LogisticChart",
            });
          if (data[0].Widget_Routing === 1)
            widgets.push({
              i: "widget5",
              x: 0,
              y: 4,
              w: 8,
              h: 2,
              type: "RoutingChart",
            });
          if (data[0].Widget_User === 1)
            widgets.push({
              i: "widget6",
              x: 0,
              y: 6,
              w: 4,
              h: 2,
              type: "UserChart",
            });
          if (data[0].Widget_Scp === 1)
            widgets.push({
              i: "widget7",
              x: 4,
              y: 6,
              w: 4,
              h: 2,
              type: "SCPChart",
            });

          console.log("Final Widgets from DB config:", widgets);
          setWidgetArray(widgets);
        }
      } catch (err) {
        console.error("fetchWidgetDetailsElse Error:", err);
      }
    }
  };

  const handleModify = (newLayouts) => {
    if (initialLoad) return;
    console.log("Layout Modified:", newLayouts);

    const tempArray = [...widgetArray];
    newLayouts.forEach((position) => {
      const widgetIndex = tempArray.findIndex(
        (widget) => widget.i === position.i
      );
      if (widgetIndex !== -1) {
        tempArray[widgetIndex].x = position.x;
        tempArray[widgetIndex].y = position.y;
        tempArray[widgetIndex].w = position.w;
        tempArray[widgetIndex].h = position.h;
      }
    });

    console.log("Updated Widget Array:", tempArray);
    setLayouts(newLayouts);
    setWidgetArray(tempArray);

    const layoutWithType = newLayouts.map((layout) => {
      const widget = tempArray.find((widget) => widget.i === layout.i);
      return {
        i: layout.i,
        x: layout.x,
        y: layout.y,
        w: layout.w,
        h: layout.h,
        type: widget ? widget.type : null,
      };
    });

    // Save to cookies
    const expires = new Date(Date.now() + 7 * 864e5).toUTCString();
    document.cookie = `${UM_UserId}=${encodeURIComponent(JSON.stringify(layoutWithType))}; expires=${expires}; path=/`;
  };

  const renderWidgetByType = (type) => {
    console.log("Rendering Widget Type:", type);
    switch (type) {
      case "OrderChart":
        return <OrderChart />;
      case "UserChart":
        return <UserChart />;
      case "ShipmentChart":
        return <ShipmentChart />;
      case "SCPChart":
        return <SCPChart />;
      case "RoutingChart":
        return <RoutingChart />;
      case "LogisticChart":
        return <LogisticChart />;
      case "InventoryChart":
        return <InventoryChart />;
      default:
        console.warn("Unknown Widget Type:", type);
        return null;
    }
  };

  console.log("WIDGET ARRAY", widgetArray);

  const normalizedWidgets = widgetArray.map((w, index) => {
    const widget = { ...w };

    if (widgetArray.length === 1) {
      widget.w = 8;
      widget.h = 2;
    } else if (widgetArray.length === 2) {
      widget.h = 2;
      widget.w = 4;
      widget.x = index * 4;
      widget.y = 0;
    } else if (widgetArray.length === 3) {
      widget.h = 2;
      if (index < 2) {
        widget.w = 4;
        widget.x = index * 4;
        widget.y = 0;
      } else {
        widget.w = 8;
        widget.x = 0;
        widget.y = 2;
      }
    } else if (widgetArray.length === 4) {
      widget.h = 2;
      widget.w = 4;
      widget.x = index % 2 === 0 ? 0 : 4;
      widget.y = index < 2 ? 0 : 2;
    }
    return widget;
  });

  return (
    <>
      <nav className="dashnavbar">
        <span>
          <img 
            src={techtrio} 
            alt="techtrio" 
            className="techtriologo"  
            onClick={() => navigate("/home")} 
            style={{ cursor: 'pointer' }}
          />
        </span>
        <div className="rightnav">
          <button className="widget_btn" onClick={() => setShowModal(true)}>
            Widget
          </button>
        </div>
      </nav>

      <div className="main_container1">
        {Utype === 3 && <Cards />}
        <ResponsiveReactGridLayout
          key={refreshKey}
          onLayoutChange={handleModify}
          verticalCompact={true}
          layout={normalizedWidgets}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          preventCollision={false}
          cols={{ lg: 8, md: 8, sm: 4, xs: 2, xxs: 2 }}
          autoSize={true}
          margin={{
            lg: [20, 20],
            md: [20, 20],
            sm: [20, 20],
            xs: [20, 20],
            xxs: [20, 20],
          }}
          draggableHandle=".drag-handle"
        >
          {normalizedWidgets.map((widget) => (
            <div
              key={widget.i}
              data-grid={widget}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f0f0f0",
                padding: "10px",
                borderRadius: "5px",
                position: "relative",
              }}
            >
              <div className="widgetContent">
                {renderWidgetByType(widget.type)}
              </div>
              <div
                className="drag-handle"
                style={{
                  position: "absolute",
                  top: "1px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "20px",
                  height: "10px",
                  backgroundColor: "#ccc",
                  cursor: "grab",
                  borderRadius: "3px",
                }}
              />
            </div>
          ))}
        </ResponsiveReactGridLayout>
      </div>

      <Widget 
        showModal={showModal} 
        setShowModal={setShowModal} 
        onWidgetChange={handleWidgetChange} 
      />
    </>
  );
};

export default ReactGridLayout;